import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import {
  GetCustomerAirtimeBalance,
  GetCustomerBundleBalance,
} from "functions/http-requests/customer";
import { useDispatch } from "react-redux";
import { toggleLoading } from "state/slice/loading";
import { BundleBalanceProps } from "_interfaces/bundle-balance";
import { AirtimeBalanceProps } from "_interfaces/airtime-balance";

const Dashboard = () => {
  const dispatch = useDispatch();

  const [bundleData, setBundleData] = useState<
    BundleBalanceProps["countersSummary"][] | null
  >(null);
  const [airtimeBalance, setAirtimeBalance] = useState<
    AirtimeBalanceProps[] | null
  >(null);
  const [refresh, setRefresh] = useState(false);
  useEffect(() => {
    dispatch(toggleLoading(true));

    Promise.all([GetCustomerBundleBalance(), GetCustomerAirtimeBalance()])
      .then(([bundleRes, airtimeRes]) => {
        setBundleData(bundleRes?.data?.data?.countersSummary ?? []);
        setAirtimeBalance(airtimeRes?.data?.data ?? []);
      })
      .catch(() => {
        setBundleData(null);
        setAirtimeBalance(null);
      })
      .finally(() => {
        dispatch(toggleLoading(false));
      });
  }, [refresh]);

  const getBalanceByType = (types: string[]): number => {
    if (!Array.isArray(bundleData)) return 0;

    return bundleData
      .filter((item): item is NonNullable<typeof item> => {
        if (!item?.counterName) return false;
        const name = item.counterName.toUpperCase().trim();
        return types.some((t) => name.includes(t));
      })
      .reduce((sum, item) => {
        const actual = Number(item.actualValue ?? 0);
        const used = Number(item.usedValue ?? 0);
        const available = actual - used;
        return sum + (available > 0 ? available : 0);
      }, 0);
  };

  const dataBalance = getBalanceByType(["DATA", "CUSTOM DATA NONEXPIRY"]);
  const voiceBalance = getBalanceByType(["ONNET MINS", "ON-NET MINUTES"]);
  const smsBalance = getBalanceByType(["SMS", "CUSTOM SMS NONEXPIRY"]);

  const formatData = (value: number): string => {
    if (value < 1) {
      return `${(value * 1024).toFixed(2)} KB`;
    } else if (value < 1024) {
      return `${value.toFixed(0)} MB`;
    } else {
      return `${(value / 1024).toFixed(2)} GB`;
    }
  };

  return (
    <Box
      sx={{
        boxShadow: "0 .25rem 1rem rgba(0,0,0,.08)",
        borderRadius: 3,
        padding: 3,
        display: "flex",
        gap: 3,
        flexDirection: "column",
        mb: 2,
      }}
    >
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Typography fontWeight={600} fontSize={20}>
            Voice
          </Typography>
          <Typography fontWeight={500} fontSize={14}>
            {Math.floor(voiceBalance)} mins
          </Typography>
        </Grid>

        <Grid item xs={12} md={4}>
          <Typography fontWeight={600} fontSize={20}>
            Data
          </Typography>
          <Typography fontWeight={500} fontSize={14}>
            {formatData(dataBalance)}
          </Typography>
        </Grid>

        <Grid item xs={12} md={4}>
          <Typography fontWeight={600} fontSize={20}>
            SMS
          </Typography>
          <Typography fontWeight={500} fontSize={14}>
            {smsBalance.toFixed(0)} messages
          </Typography>
        </Grid>

        <Grid item xs={12} md={4}>
          <Typography fontWeight={600} fontSize={20}>
            Airtime Balance
          </Typography>
          <Typography fontWeight={500} fontSize={14}>
            UGX {Math.floor(airtimeBalance?.[0]?.balance ?? 0)}
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
